import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, Route, Redirect, RouteProps } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { AppsIcon, AccountIcon, UsersIcon, TeamsIcon } from './components';
import { AnalyticsIcon } from './components/Icons/AnalyticsIcon';
import { MyOrgIcon } from './components/Icons/MyOrgIcon';
import { OrganizationsIcon } from './components/Icons/OrganizationsIcon';
import { SubrouteIcon } from './components/Icons/SubrouteIcon';
import { Account, Apps, Teams, Users } from './pages';
import { Analytics } from './pages/Analytics/Analytics';
import { GoogleCallback } from './pages/GoogleCallback/GoogleCallback';
import { MyOrganization } from './pages/MyOrganization/MyOrganization';
import { Organizations } from './pages/Organizations/Organizations';
import Sub2 from './pages/sub2';
import Sub3 from './pages/sub3';
import { refreshToken } from './store/auth/auth.actions';
import { RootState } from './store/store';
import { RouteI, SubRoutes, User } from './types';

export const ROUTES: (RouteI | SubRoutes)[] = [
  {
    path: `/orgs/:orgId/users/:userId`,
    Component: Account,
    label: 'Account',
    Icon: AccountIcon,
    roles: ['dealmaker', 'owner', 'admin', 'superAdmin'],
  },
  {
    path: `/orgs/:orgId/users`,
    Component: Users,
    label: 'Users',
    Icon: UsersIcon,
    roles: ['owner', 'admin', 'superAdmin'],
  },
  {
    path: `/orgs/:orgId/apps`,
    Component: Apps,
    label: 'Apps',
    Icon: AppsIcon,
    roles: ['dealmaker', 'owner', 'admin', 'superAdmin'],
  },
  {
    path: '/orgs',
    Component: Organizations,
    label: 'Organizations',
    Icon: OrganizationsIcon,
    roles: ['superAdmin'],
  },
  {
    path: '/orgs/:orgId',
    Component: MyOrganization,
    label: 'My Organization',
    Icon: MyOrgIcon,
    roles: ['owner', 'admin', 'superAdmin'],
  },
  {
    path: `/orgs/:orgId/teams`,
    Component: Teams,
    label: 'Teams',
    Icon: TeamsIcon,
    roles: ['dealmaker', 'owner', 'admin', 'superAdmin'],
    disabled: true,
  },
  {
    path: '/orgs/:orgId/users/:userId/personalq',
    Component: Analytics,
    label: 'Personal Q',
    Icon: AnalyticsIcon,
    roles: ['dealmaker', 'owner', 'admin', 'superAdmin'],
    // subroutes: [
    //   {
    //     label: 'Personal Q',
    //     path: 'personalq',
    //     Component: Sub2,
    //     Icon: SubrouteIcon,
    //     roles: ['dealmaker', 'owner', 'admin', 'superAdmin'],
    //   },
    //   {
    //     label: 'subroute 2',
    //     path: '/orgs/:orgId/analytics/subroute2',
    //     Component: Sub3,
    //     Icon: SubrouteIcon,
    //     roles: ['dealmaker', 'owner', 'admin', 'superAdmin'],
    //   },
    // ],
  },
];

interface ProtectedRouteProps extends RouteProps {
  roles: string[];
  user: User | null;
  children: JSX.Element;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  roles,
  user,
  children,
  ...props
}: ProtectedRouteProps): JSX.Element | null => {
  if (!localStorage.getItem('refresh')) return <Redirect to="/" />;

  return roles.includes(user?.role as string) && user?.isEmailVerified ? (
    <Route {...props}>
      {({ match }) => (
        <CSSTransition timeout={500} classNames="routes" in={match != null}>
          {children}
        </CSSTransition>
      )}
    </Route>
  ) : null;
};

export const AppRouting: React.FC = (): JSX.Element | null => {
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!user && localStorage.getItem('token')) {
      dispatch(refreshToken());
    } // eslint-disable-next-line
  }, []);

  return (
    <TransitionGroup>
      <Switch>
        {ROUTES.map((route) => {
          if ('subroutes' in route)
            return (
              <React.Fragment key={route.path}>
                <ProtectedRoute {...route} user={user} key={route.label}>
                  <>
                    {route.subroutes &&
                      route.subroutes.map(({ Component, ...r }) => (
                        <React.Fragment key={r.path}>
                          <ProtectedRoute {...r} user={user}>
                            <Component />
                          </ProtectedRoute>
                        </React.Fragment>
                      ))}
                  </>
                </ProtectedRoute>
              </React.Fragment>
            );

          const { path, Component, roles } = route;
          return (
            <ProtectedRoute
              roles={roles}
              key={path}
              exact
              path={path}
              user={user}
            >
              <Component />
            </ProtectedRoute>
          );
        })}
        <Route path="/google-callback">
          <GoogleCallback />
        </Route>
        <Route path="*">
          <Redirect to="/not-found" />
        </Route>
      </Switch>
    </TransitionGroup>
  );
};
